import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, } from 'chart.js';
// Register the annotation plugin
const annotationPlugin = {
    id: 'annotation',
    beforeDraw: (chart) => {
        const { ctx, chartArea, scales } = chart;
        if (!chartArea)
            return;
        // Status lines following the getMetricStatus pattern
        const statusLines = [
            { value: 100, color: '#22c55e', label: 'Excellent' }, // Strong green (100% target)
            { value: 85, color: '#fb923c', label: 'Warning' }, // Strong orange (warning starts at 85%)
            { value: 60, color: '#ef4444', label: 'Critical' }, // Strong red (critical below 60%)
        ];
        statusLines.forEach(({ value, color }) => {
            const yPosition = scales.y.getPixelForValue(value);
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([]); // Solid lines (no dash)
            ctx.lineWidth = 3; // Thick lines
            ctx.strokeStyle = color;
            ctx.moveTo(chartArea.left, yPosition);
            ctx.lineTo(chartArea.right, yPosition);
            ctx.stroke();
            ctx.restore();
        });
    },
};
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, annotationPlugin);
const DeviceTrendsChart = memo(({ data, title = 'Device Trends', height = 400, className = '', selectedMetrics: propSelectedMetrics, }) => {
    const { isDark } = useTheme();
    const [selectedMetrics, setSelectedMetrics] = useState(['oee']);
    // Update selected metrics when props change (only use first metric for single selection)
    useEffect(() => {
        if (propSelectedMetrics && propSelectedMetrics.length > 0) {
            setSelectedMetrics([propSelectedMetrics[0]]);
        }
    }, [propSelectedMetrics]);
    const metricColors = {
        oee: 'rgb(59, 130, 246)',
        availability: 'rgb(34, 197, 94)',
        performance: 'rgb(251, 146, 60)',
        quality: 'rgb(168, 85, 247)',
    };
    const metricLabels = {
        oee: 'OEE',
        availability: 'Availability',
        performance: 'Performance',
        quality: 'Quality',
        deviceId: 'Device ID',
        deviceName: 'Device Name',
        timestamp: 'Timestamp',
    };
    const processedData = useMemo(() => {
        if (!data || data.length === 0) {
            return { dates: [], datasets: [] };
        }
        try {
            // Group data by date and device
            const groupedData = {};
            data.forEach((item) => {
                if (!item.timestamp || !item.deviceId)
                    return;
                const dateObj = new Date(item.timestamp);
                if (isNaN(dateObj.getTime()))
                    return;
                const date = dateObj.toISOString().split('T')[0];
                if (!groupedData[date]) {
                    groupedData[date] = {};
                }
                if (!groupedData[date][item.deviceId] ||
                    dateObj > new Date(groupedData[date][item.deviceId].timestamp)) {
                    groupedData[date][item.deviceId] = item;
                }
            });
            const dates = Object.keys(groupedData).sort();
            // Create datasets for selected metrics
            const datasets = selectedMetrics.flatMap((metric) => {
                const uniqueDevices = [
                    ...new Set(data.filter((d) => d.deviceId).map((d) => d.deviceId)),
                ];
                return uniqueDevices.map((deviceId, deviceIndex) => {
                    const device = data.find((d) => d.deviceId === deviceId);
                    const deviceName = (device === null || device === void 0 ? void 0 : device.deviceName) || `Device ${deviceIndex + 1}`;
                    const color = [
                        'rgb(59, 130, 246)',
                        'rgb(34, 197, 94)',
                        'rgb(251, 146, 60)',
                        'rgb(168, 85, 247)',
                        'rgb(239, 68, 68)',
                    ][deviceIndex % 5];
                    return {
                        label: `${deviceName} - ${metricLabels[metric]}`,
                        data: dates.map((date) => {
                            var _a;
                            const deviceItem = (_a = groupedData[date]) === null || _a === void 0 ? void 0 : _a[deviceId];
                            return deviceItem ? deviceItem[metric] : null;
                        }),
                        borderColor: color,
                        backgroundColor: color
                            .replace('rgb', 'rgba')
                            .replace(')', ', 0.1)'),
                        borderWidth: 2,
                        fill: false,
                        tension: 0.4,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    };
                });
            });
            return { dates, datasets };
        }
        catch (error) {
            console.error('Error processing chart data:', error);
            return { dates: [], datasets: [] };
        }
    }, [data, selectedMetrics]);
    const chartData = {
        labels: processedData.dates,
        datasets: processedData.datasets,
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                    usePointStyle: true,
                    padding: 15,
                },
            },
            title: {
                display: true,
                text: title,
                font: {
                    size: 16,
                    weight: 'bold',
                },
                color: isDark ? 'rgb(243, 244, 246)' : 'rgb(17, 24, 39)',
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function (context) {
                        var _a;
                        return `${context.dataset.label}: ${(_a = context.parsed.y) === null || _a === void 0 ? void 0 : _a.toFixed(1)}%`;
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Date',
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                ticks: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                grid: {
                    color: isDark ? 'rgb(55, 65, 81)' : 'rgb(229, 231, 235)',
                },
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: 'Percentage (%)',
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                min: 0,
                max: 105,
                ticks: {
                    callback: function (value) {
                        if (value > 100)
                            return '';
                        if (value === 100)
                            return '100%';
                        return value + '%';
                    },
                    color: function (context) {
                        if (context.tick.value === 100) {
                            return 'rgb(239, 68, 68)';
                        }
                        return isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)';
                    },
                },
                grid: {
                    display: false, // Remove horizontal grid lines
                },
            },
        },
    };
    const toggleMetric = useCallback((metric) => {
        setSelectedMetrics((prev) => prev.includes(metric) ? prev.filter((m) => m !== metric) : [metric] // Replace all metrics with the selected one
        );
    }, []);
    const MetricButton = ({ metric, label, color }) => (_jsx("button", { onClick: () => toggleMetric(metric), className: `px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${selectedMetrics.includes(metric)
            ? 'text-white shadow-lg'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`, style: {
            backgroundColor: selectedMetrics.includes(metric) ? color : undefined,
        }, children: label }));
    return (_jsxs("div", { className: `bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 ${className}`, children: [_jsxs("div", { className: "mb-4", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900 dark:text-white mb-3", children: "Performance Metrics" }), _jsx("p", { className: "text-sm text-gray-600 dark:text-gray-400 mb-3", children: "Showing devices with active sensors for the last 20 days. Toggle metrics to compare OEE, Availability, Performance, and Quality across devices." }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx(MetricButton, { metric: "oee", label: "OEE", color: metricColors.oee }), _jsx(MetricButton, { metric: "availability", label: "Availability", color: metricColors.availability }), _jsx(MetricButton, { metric: "performance", label: "Performance", color: metricColors.performance }), _jsx(MetricButton, { metric: "quality", label: "Quality", color: metricColors.quality })] })] }), _jsx("div", { style: { height }, children: _jsx(Line, { data: chartData, options: options }) })] }));
});
DeviceTrendsChart.displayName = 'DeviceTrendsChart';
export default DeviceTrendsChart;
